import {
  computed,
  signal,
  type Signal,
  type WritableSignal,
} from '../reactive/arr-waveform-store';
export interface ArrWaveformState {
  readonly currentTime: Signal<number>;
  readonly duration: Signal<number>;
  readonly isPlaying: Signal<boolean>;
  readonly isPaused: Signal<boolean>;
  readonly isSeeking: Signal<boolean>;
  readonly volume: Signal<number>;
  readonly playbackRate: Signal<number>;
  readonly audioBuffer: Signal<AudioBuffer | null>;
  readonly peaks: Signal<Array<Float32Array | number[]> | null>;
  readonly url: Signal<string>;
  readonly zoom: Signal<number>;
  readonly scrollPosition: Signal<number>;
  readonly canPlay: Signal<boolean>;
  readonly isReady: Signal<boolean>;
  readonly progress: Signal<number>;
  readonly progressPercent: Signal<number>;
}
export interface ArrWaveformActions {
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setPlaying: (playing: boolean) => void;
  setSeeking: (seeking: boolean) => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  setAudioBuffer: (buffer: AudioBuffer | null) => void;
  setPeaks: (peaks: Array<Float32Array | number[]> | null) => void;
  setUrl: (url: string) => void;
  setZoom: (zoom: number) => void;
  setScrollPosition: (position: number) => void;
}
export interface PlayerSignals {
  isPlaying?: WritableSignal<boolean>;
  currentTime?: WritableSignal<number>;
  duration?: WritableSignal<number>;
  volume?: WritableSignal<number>;
  playbackRate?: WritableSignal<number>;
  isSeeking?: WritableSignal<boolean>;
}
export function createArrWaveformState(playerSignals?: PlayerSignals): {
  state: ArrWaveformState;
  actions: ArrWaveformActions;
} {
  const currentTime = playerSignals?.currentTime ?? signal(0);
  const duration = playerSignals?.duration ?? signal(0);
  const isPlaying = playerSignals?.isPlaying ?? signal(false);
  const isSeeking = playerSignals?.isSeeking ?? signal(false);
  const volume = playerSignals?.volume ?? signal(1);
  const playbackRate = playerSignals?.playbackRate ?? signal(1);
  const audioBuffer = signal<AudioBuffer | null>(null);
  const peaks = signal<Array<Float32Array | number[]> | null>(null);
  const url = signal('');
  const zoom = signal(0);
  const scrollPosition = signal(0);
  const isPaused = computed(() => !isPlaying.value, [isPlaying]);
  const canPlay = computed(() => audioBuffer.value !== null, [audioBuffer]);
  const isReady = computed(() => {
    return canPlay.value && duration.value > 0;
  }, [canPlay, duration]);
  const progress = computed(() => currentTime.value, [currentTime]);
  const progressPercent = computed(() => {
    return duration.value > 0 ? currentTime.value / duration.value : 0;
  }, [currentTime, duration]);
  const state: ArrWaveformState = {
    currentTime,
    duration,
    isPlaying,
    isPaused,
    isSeeking,
    volume,
    playbackRate,
    audioBuffer,
    peaks,
    url,
    zoom,
    scrollPosition,
    canPlay,
    isReady,
    progress,
    progressPercent,
  };
  const actions: ArrWaveformActions = {
    setCurrentTime: (time: number) => {
      const clampedTime = Math.max(
        0,
        Math.min(duration.value || Infinity, time)
      );
      currentTime.set(clampedTime);
    },
    setDuration: (d: number) => {
      duration.set(Math.max(0, d));
    },
    setPlaying: (playing: boolean) => {
      isPlaying.set(playing);
    },
    setSeeking: (seeking: boolean) => {
      isSeeking.set(seeking);
    },
    setVolume: (v: number) => {
      const clampedVolume = Math.max(0, Math.min(1, v));
      volume.set(clampedVolume);
    },
    setPlaybackRate: (rate: number) => {
      const clampedRate = Math.max(0.1, Math.min(16, rate));
      playbackRate.set(clampedRate);
    },
    setAudioBuffer: (buffer: AudioBuffer | null) => {
      audioBuffer.set(buffer);
      if (buffer) {
        duration.set(buffer.duration);
      }
    },
    setPeaks: (p: Array<Float32Array | number[]> | null) => {
      peaks.set(p);
    },
    setUrl: (u: string) => {
      url.set(u);
    },
    setZoom: (z: number) => {
      zoom.set(Math.max(0, z));
    },
    setScrollPosition: (pos: number) => {
      scrollPosition.set(Math.max(0, pos));
    },
  };
  return {state, actions};
}
